import * as moment from 'moment';

/**
 * Fallback host for the PMU Racing Data silk/casaque asset service, used
 * only when `config.json`'s `PROXIES.casaque_baseUrl` is unavailable.
 * Callers should prefer passing the configured base URL (`ConfigService`'s
 * `API_ADDRESSES.casaque_baseUrl`) into `buildSilkUrl` rather than relying
 * on this constant. `casaque_baseUrl` now holds the Racing Data silk host
 * directly — it replaced the legacy `pmu.fr/back-assets` URL it used to
 * carry, rather than living alongside a separate `silk_baseUrl` key.
 * @see buildSilkUrl
 */
export const SILK_BASE_URL = 'https://assets.racingdata.pmu.fr/silks';

/**
 * Fallback racecourse code used when neither the backend's `str_Code`
 * (`Race.racecourseCode`) nor a `reunion_code.json` alias match is
 * available for a race's meeting name. See `PmuService.getRacecourseCode`.
 */
export const DEFAULT_RACECOURSE_CODE = 'CAP';

/**
 * Strips a leading letter prefix (e.g. "C8" -> "8", "P7" -> "7") that the
 * legacy PMU format used for race/participant numbers. Already-numeric
 * input passes through unchanged.
 */
function toNumericToken(value: number | string): string {
  const numeric = String(value).match(/\d+/);
  return numeric ? numeric[0] : String(value);
}

/**
 * Builds a PMU Racing Data silk/casaque asset URL:
 *
 *   {baseUrl}/{YYYYMMDD}/{racecourseCode}/{raceNumber}/{YYYYMMDD}-{racecourseCode}-{raceNumber}-{participantNumber}.png
 *
 * `baseUrl` should be the configured `PROXIES.casaque_baseUrl` from
 * `config.json` (via `ConfigService.API_ADDRESSES.casaque_baseUrl`) — it
 * only falls back to the `SILK_BASE_URL` constant when omitted.
 *
 * `racecourseCode` is the Racing Data racecourse/meeting code (e.g. "CAP")
 * for the race being requested. Call sites resolve it via
 * `PmuService.getRacecourseCode(meetingName, race.racecourseCode)`, which
 * prefers the backend's `str_Code` (when the API supplies it on the `HP`
 * reunion entry) and otherwise falls back to a substring match against
 * `public/statics/reunion_code.json`, defaulting to `DEFAULT_RACECOURSE_CODE`
 * if nothing matches. `raceNumber`/`participantNumber` may be passed either
 * as plain numbers or as legacy-prefixed strings ("C8", "P7") — the "C"/"P"
 * prefix is stripped automatically.
 *
 * @example
 * buildSilkUrl('2026-08-25', 'CAP', 8, 7)
 * // "https://assets.racingdata.pmu.fr/silks/20260825/CAP/8/20260825-CAP-8-7.png"
 */
export function buildSilkUrl(
  date: string | Date,
  racecourseCode: string,
  raceNumber: number | string,
  participantNumber: number | string,
  baseUrl: string = SILK_BASE_URL
): string {
  const formattedDate = moment(date).format('YYYYMMDD');
  const race = toNumericToken(raceNumber);
  const participant = toNumericToken(participantNumber);
  const host = baseUrl.replace(/\/+$/, '');

  return `${host}/${formattedDate}/${racecourseCode}/${race}/${formattedDate}-${racecourseCode}-${race}-${participant}.png`;
}
